import imageUrlBuilder from '@sanity/image-url';

const projectId = '0yclmaja';
const dataset = 'production';
const apiVersion = '2023-05-03';

const builder = imageUrlBuilder({ projectId, dataset });

export function urlFor(source) {
  return builder.image(source);
}

async function runGroqQuery(query, params = {}) {
  let paramStr = '';
  Object.keys(params).forEach(key => {
    paramStr += `&$${key}=${encodeURIComponent(JSON.stringify(params[key]))}`;
  });

  const isBrowser = typeof window !== 'undefined';
  const isLocal = isBrowser && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const apiHost = (isBrowser && !isLocal) ? `${window.location.origin}/sanity-api` : `https://${projectId}.api.sanity.io`;
  const url = `${apiHost}/v${apiVersion}/data/query/${dataset}?query=${encodeURIComponent(query)}${paramStr}`;

  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Sanity fetch status: ${res.status}`);
    }
    const data = await res.json();
    return data.result;
  } catch (err) {
    if (isBrowser) {
      try {
        const directUrl = `https://${projectId}.api.sanity.io/v${apiVersion}/data/query/${dataset}?query=${encodeURIComponent(query)}${paramStr}`;
        const directRes = await fetch(directUrl);
        const directData = await directRes.json();
        return directData.result;
      } catch (directErr) {
        console.error("Direct Sanity query failed:", directErr);
      }
    }
    console.error('runGroqQuery error:', err);
    return null;
  }
}

export async function getPosts(categorySlug = null, searchQuery = '', lang = 'en') {
  try {
    let filters = [`_type == "post"`, `(language == $lang || ($lang == "en" && !defined(language)))`].filter(Boolean);
    const params = { lang };

    if (categorySlug) {
      filters.push(`(category == $cat || category->slug.current == $cat || categoryRef->slug.current == $cat)`);
      params.cat = categorySlug;
    }

    if (searchQuery && searchQuery.trim() !== '') {
      filters.push(`(title match $q || excerpt match $q)`);
      params.q = `*${searchQuery.trim()}*`;
    }

    const query = `*[${filters.join(' && ')}] | order(date desc, _createdAt desc) [0...50] {
      _id,
      title,
      slug,
      excerpt,
      "category": coalesce(category->title, category, "General"),
      "categorySlug": coalesce(category->slug.current, categoryRef->slug.current, category),
      date,
      readTime,
      mainImage,
      language
    }`;

    let results = await runGroqQuery(query, params);

    // Fallback: If no posts found in current language, fallback to English posts so the section is never blank
    if ((!results || results.length === 0) && lang !== 'en') {
      const fallbackParams = { ...params, lang: 'en' };
      const fallbackQuery = `*[${filters.join(' && ')}] | order(date desc, _createdAt desc) [0...50] {
        _id,
        title,
        slug,
        excerpt,
        "category": coalesce(category->title, category, "General"),
        "categorySlug": coalesce(category->slug.current, categoryRef->slug.current, category),
        date,
        readTime,
        mainImage,
        language
      }`;
      results = await runGroqQuery(fallbackQuery, fallbackParams);
    }

    return results || [];
  } catch (err) {
    console.error('Error fetching posts:', err);
    return [];
  }
}

export async function getPostBySlug(slug) {
  try {
    const query = `*[_type == "post" && slug.current == $slug][0] {
      _id,
      title,
      slug,
      excerpt,
      "category": coalesce(category->title, category, "General"),
      "categorySlug": coalesce(category->slug.current, categoryRef->slug.current, category),
      author,
      date,
      readTime,
      mainImage,
      body,
      language
    }`;
    return await runGroqQuery(query, { slug });
  } catch (err) {
    console.error('Error fetching post by slug:', err);
    return null;
  }
}

export async function getCategories(lang = 'en') {
  try {
    const categoriesQuery = `*[_type == "category"] {
      _id,
      title,
      "slug": slug.current
    }`;
    const categories = await runGroqQuery(categoriesQuery) || [];

    const postsCountQuery = `*[_type == "post" && (language == $lang || ($lang == "en" && !defined(language)))] {
      "catSlug": coalesce(category->slug.current, categoryRef->slug.current, category)
    }`;
    const posts = await runGroqQuery(postsCountQuery, { lang }) || [];

    const counts = {};
    posts.forEach(p => {
      if (p.catSlug && typeof p.catSlug === 'string') {
        counts[p.catSlug] = (counts[p.catSlug] || 0) + 1;
      }
    });

    return categories.map(cat => ({
      ...cat,
      count: counts[cat.slug] || 0
    }));
  } catch (err) {
    console.error('Error fetching categories:', err);
    return [];
  }
}
